import { useStableCallback } from "@prismicio/editor-support/React"
import { type ReactNode, useEffect } from "react"

import { Box } from "../Box"
import { Button } from "../Button"
import { Icon, type IconName } from "../Icon"
import { IconButton } from "../IconButton"
import { Popover } from "../Popover"
import { ProgressCircle } from "../ProgressCircle"
import { Text } from "../Text"
import styles from "./Toast.module.css"

interface ToastAction {
  title: string
  onClick: () => void
  icon?: IconName
}

interface ToastCancel {
  onClick: () => void
}

interface InlineToastProps {
  variant?: "inline"
  icon?: IconName
  loadingIndicator?: boolean
}

interface CardToastProps {
  variant: "card"
  subtitle?: ReactNode
}

export type ToastProps = {
  anchor: ReactNode
  open: boolean
  title: ReactNode
  seconds?: number
  action?: ToastAction
  cancel?: ToastCancel
  onOpenChange?: (open: boolean) => void
} & (InlineToastProps | CardToastProps)

/**
 * We don't use the radix ui toast because of a bug with the timeout.
 * Also, in general, there are lots of issues around radix's Toast in their bug tracker and they don't fix much.
 * Example: https://github.com/radix-ui/primitives/pull/2236
 * It also doesn't help at all when it comes to providing an imperative API: https://github.com/radix-ui/primitives/issues/2804
 * They also never finished implementing it: https://github.com/radix-ui/primitives/issues/1171
 */
export function Toast(props: ToastProps) {
  const { anchor, open, title, seconds, action, cancel, onOpenChange } = props

  const stableOnOpenChange = useStableCallback(onOpenChange)

  useEffect(() => {
    if (!open || !seconds) return

    const timeout = setTimeout(() => {
      stableOnOpenChange(false)
    }, seconds * 1000)

    return () => clearTimeout(timeout)
  }, [seconds, open, stableOnOpenChange])

  return (
    <Popover
      anchor={anchor}
      open={open}
      // We don't want to focus the portal when the toast opens.
      focusOnEnter={false}
      onOpenChange={onOpenChange}
      onInteractOutside={(event) => event.preventDefault()}
      onEscapeKeyDown={(event) => {
        // Prevent it from closing with `esc` if there is an action.
        if (action) event.preventDefault()
      }}
    >
      {props.variant === "card" ? (
        <CardToast
          title={title}
          subtitle={props.subtitle}
          action={action}
          cancel={cancel}
          onOpenChange={onOpenChange}
        />
      ) : (
        <InlineToast
          icon={props.icon}
          loadingIndicator={props.loadingIndicator}
          title={title}
          action={action}
          cancel={cancel}
          onOpenChange={onOpenChange}
        />
      )}
    </Popover>
  )
}

interface InlineToastContentProps {
  icon?: IconName
  loadingIndicator?: boolean
  title: ReactNode
  action?: ToastAction
  cancel?: ToastCancel
  onOpenChange?: (open: boolean) => void
}

function InlineToast(props: InlineToastContentProps) {
  const { icon, loadingIndicator, title, action, cancel, onOpenChange } = props

  return (
    <div className={styles.inline}>
      <div className={styles.content}>
        {!!icon && <Icon name={icon} color="grey11" size="medium" />}
        {loadingIndicator && <ProgressCircle color="dark12" />}
        <Text variant="normal" color="white">
          {title}
        </Text>
      </div>
      {(action || cancel) && (
        <>
          <div className={styles.separator} />
          {action && (
            <Button
              invisible
              color="white"
              onClick={() => {
                onOpenChange?.(false)
                action.onClick()
              }}
              endIcon={action.icon}
            >
              {action.title}
            </Button>
          )}
          {cancel && (
            <IconButton
              icon="close"
              size="medium"
              hasPadding={false}
              onClick={cancel.onClick}
            />
          )}
        </>
      )}
    </div>
  )
}

interface CardToastContentProps {
  title: ReactNode
  subtitle?: ReactNode
  action?: ToastAction
  cancel?: ToastCancel
  onOpenChange?: (open: boolean) => void
}

function CardToast(props: CardToastContentProps) {
  const { title, subtitle, action, onOpenChange, cancel } = props

  function close() {
    cancel?.onClick()
    onOpenChange?.(false)
  }

  return (
    <div className={styles.card}>
      <div className={styles.closeButton}>
        <IconButton
          icon="close"
          size="medium"
          hasPadding={false}
          onClick={close}
        />
      </div>
      {subtitle ? (
        <Box flexDirection="column" alignItems="flex-start">
          <Text variant="bold">{title}</Text>
          <Text>{subtitle}</Text>
        </Box>
      ) : (
        <Text variant="normal">{title}</Text>
      )}
      {action && (
        <Button
          size="medium"
          onClick={() => {
            onOpenChange?.(false)
            action.onClick()
          }}
          endIcon={action.icon}
        >
          {action.title}
        </Button>
      )}
    </div>
  )
}
